import React, {useState, useEffect} from 'react';
import {useSelector, useDispatch} from 'react-redux';
import {useHistory, useLocation} from 'react-router-dom';
import useDeepCompareEffect from 'use-deep-compare-effect';
import moment from 'moment-timezone';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import {DataGridPro, GridActionsCellItem} from '@mui/x-data-grid-pro';
import {faEllipsisHAlt} from '@fortawesome/pro-light-svg-icons';
import {faPlus} from '@fortawesome/pro-regular-svg-icons';

import PrimaryAppBar from './PrimaryAppBar';
import SecondaryAppBar from './SecondayAppBar';
import SnackbarSuccess from './SnackbarSuccess';
import StudentMenu from './StudentMenu';
import DataGridQuickSearchToolbar from './DataGridQuickSearchToolbar';
import {fetchStudents, selectStudents, selectIsLoading as selectIsBuilderLoading} from '../../redux/slices/builder';
import {selectUserGroups, selectUsers, selectIsLoading as selectIsUserLoading} from '../../redux/slices/user';
import ImageNoData from '../images/no-data.svg';
import {capitalizeWord} from '../../helpers';

import styles from '../css/Students.module.css';
import baseStyles from '../../css/Base.module.css';

export default function Students() {
  const history = useHistory();
  const dispatch = useDispatch();
  const location = useLocation();

  const [isSnackbarOpen, toggleSnackbar] = useState(false);
  const [isMenuOpen, toggleMenu] = useState(false);
  const [activeStudent, setActiveStudent] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [studentsClone, setStudentsClone] = useState([]); // TODO: Refactor this away
  const [searchText, setSearchText] = useState('');
  const [dataGridToolbarFilterButtonEl, setDataGridToolbarFilterButtonEl] = useState(false);

  const students = useSelector(selectStudents);
  const userGroups = useSelector(selectUserGroups);
  const users = useSelector(selectUsers);
  const isBuilderLoading = useSelector(selectIsBuilderLoading);
  const isUserLoading = useSelector(selectIsUserLoading);

  const isLoading = isBuilderLoading || isUserLoading;

  // Quick search toolbar BEGIN
  function escapeRegExp(value) {
    return value.replace(/[-[\]{}()*+?.,\\^$|#\s]/g, '\\$&');
  }

  const requestSearch = (searchValue) => {
    setSearchText(searchValue);
    const searchRegex = new RegExp(escapeRegExp(searchValue), 'i');
    const filteredRows = students.filter((row) => {
      return Object.keys(row).some((field) => {
        let searchValue;
        if (field === 'tags') searchValue = row[field].map((tag) => tag.name).join(',');
        else searchValue = row[field]?.toString() ?? '';

        return searchRegex.test(searchValue);
      });
    });
    setStudentsClone(filteredRows || []);
  };
  // Quick search toolbar END

  useEffect(() => {
    if (location.state?.isSaved) {
      toggleSnackbar(true);
      history.replace({pathname: location.pathname, state: {...location.state, isSaved: false}}); // Prevent showing snack bar on refresh
    }
  }, [location, history]);

  useEffect(() => {
    setStudentsClone([]);
    dispatch(fetchStudents());
  }, [dispatch]);

  useDeepCompareEffect(() => {
    setStudentsClone(students);
  }, [students, users, userGroups, isBuilderLoading]);

  function handleClickMenu(event, student) {
    setActiveStudent(student);
    setAnchorEl(event.currentTarget);
    toggleMenu(true);
    event.stopPropagation();
  }

  function navigateToCreate() {
    history.push(`/builder/student/create`);
  }

  return (
    <>
      <SnackbarSuccess text="Student Saved." isSnackbarOpen={isSnackbarOpen} toggleSnackbar={toggleSnackbar} />
      <PrimaryAppBar />
      <SecondaryAppBar title="Students">
        <IconButton
          onClick={navigateToCreate}
          className="bg-success"
          size="large"
          sx={{'&:hover': {backgroundColor: 'rgba(66, 184, 94, 0.8)'}}}
        >
          <FontAwesomeIcon icon={faPlus} size="xs" className="clr-gray-200" />
        </IconButton>
      </SecondaryAppBar>

      <StudentMenu
        anchorEl={anchorEl}
        isMenuOpen={isMenuOpen}
        toggleMenu={toggleMenu}
        student={activeStudent}
        isEditVisible
      />

      {isLoading && (
        <div className={baseStyles['progress-container']}>
          <CircularProgress />
        </div>
      )}

      {!isLoading && students.length === 0 && (
        <div className={styles.empty}>
          <img src={ImageNoData} alt="No Lessons" className={'m-t-30 m-b-30'} />
          <Button className={baseStyles['btn-pill']} variant="outlined" onClick={navigateToCreate}>
            NEW STUDENT
          </Button>
        </div>
      )}

      {!isLoading && students?.length > 0 && (
        <div style={{height: 'calc(100vh - 117px)', width: '100%'}}>
          <div style={{display: 'flex', height: '100%'}}>
            <div style={{flexGrow: 1}}>
              <DataGridPro
                pagination
                components={{Toolbar: DataGridQuickSearchToolbar}}
                componentsProps={{
                  toolbar: {
                    value: searchText,
                    onChange: (event) => requestSearch(event.target.value),
                    clearSearch: () => requestSearch(''),
                    setFilterButtonEl: setDataGridToolbarFilterButtonEl,
                  },
                  panel: {
                    anchorEl: dataGridToolbarFilterButtonEl,
                  },
                }}
                rows={studentsClone}
                columns={[
                  {
                    field: 'action',
                    type: 'actions',
                    hideable: false,
                    getActions: (params) => [
                      <GridActionsCellItem
                        icon={<FontAwesomeIcon icon={faEllipsisHAlt} className="clr-dark-blue" />}
                        onClick={(event) => handleClickMenu(event, params.row)}
                        label="Menu"
                      />,
                    ],
                  },
                  {
                    field: 'name',
                    headerName: 'Name',
                    width: 250,
                    valueGetter: (params) =>
                      `${capitalizeWord(params.row.lastName)}, ${capitalizeWord(params.row.firstName)}`,
                    renderCell: (params) => {
                      return (
                        <Button
                          href={`/student/dashboard?authCode=${params.row.authCode}`}
                          className="text-notransform"
                        >
                          {params.value}
                        </Button>
                      );
                    },
                  },
                  {
                    field: 'birthDate',
                    headerName: 'Birthdate',
                    width: 150,
                    type: 'date',
                    valueFormatter: (params) => moment(params.value).format('M/D/YYYY'),
                  },
                  {field: 'authCode', headerName: 'Join Code', width: 250},
                  {
                    field: 'tags',
                    headerName: 'Tags',
                    width: 300,
                    valueGetter: (params) => params.row.tags.map((tag) => tag.name).join(','),
                    renderCell: (params) => {
                      const {tags} = params.row;
                      return (
                        <Stack direction="row" spacing={1} sx={{overflowX: 'auto'}}>
                          {tags.map((tag) => (
                            <Chip key={tag._id} label={tag.name} />
                          ))}
                        </Stack>
                      );
                    },
                  },
                ]}
              />
            </div>
          </div>
        </div>
      )}
    </>
  );
}
